/**
 * Panel admin de Caseta de Feria (Idea 13 ROADMAP).
 *
 * Tres pestañas:
 *   - Bonos: prepago QR consumible en barra
 *   - Mesas: reservas por fecha/hora con estado
 *   - Invitados: lista para control de puerta con check-in
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import api from '../api/client';
import TopBar from '../components/TopBar';
import toast from 'react-hot-toast';


type Tab = 'bonos' | 'mesas' | 'invitados';


export default function CasetaAdminPage() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get('t') || '';
  const [tab, setTab] = useState<Tab>('bonos');
  const [casetaTitle, setCasetaTitle] = useState('');

  useEffect(() => {
    if (!slug || !token) return;
    api.get(`/public/group-buys/admin/${slug}?t=${token}`)
      .then((r) => setCasetaTitle(r.data.title || ''))
      .catch(() => {});
  }, [slug, token]);

  if (!slug || !token) {
    return <Shell title="Falta token">Necesitas un magic link válido.</Shell>;
  }

  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      <TopBar contextLabel="Caseta · panel" rightCta={{ label: 'Mis cobros', to: '/mis-cobros' }} />
      <header className="bg-paper border-b border-line">
        <div className="max-w-5xl mx-auto px-6 py-7">
          <h1 className="font-display font-bold text-[32px] text-ink tracking-[-0.02em]">
            {casetaTitle || 'Caseta'}
          </h1>
          <p className="text-[12.5px] text-ink-500 mt-1">
            · Panel de caseta · gestiona bonos, mesas e invitados
          </p>

          <div className="mt-5 flex gap-1 border-b border-line">
            {(['bonos', 'mesas', 'invitados'] as Tab[]).map((tName) => (
              <button
                key={tName}
                type="button"
                onClick={() => setTab(tName)}
                className={`px-4 py-2 -mb-px text-[13px] font-semibold border-b-2 transition ${
                  tab === tName
                    ? 'border-brand text-brand'
                    : 'border-transparent text-ink-500 hover:text-ink'
                }`}
              >
                {tName === 'bonos' ? 'Bonos' : tName === 'mesas' ? 'Mesas' : 'Invitados'}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {tab === 'bonos' && <BonosTab slug={slug} token={token} />}
        {tab === 'mesas' && <MesasTab slug={slug} token={token} />}
        {tab === 'invitados' && <InvitadosTab slug={slug} token={token} />}
      </main>
    </div>
  );
}


function BonosTab({ slug, token }: { slug: string; token: string }) {
  const [data, setData] = useState<any>(null);
  const [holderName, setHolderName] = useState('');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [consumeFor, setConsumeFor] = useState<string | null>(null);
  const [consumeAmt, setConsumeAmt] = useState('');
  const [consumeConcept, setConsumeConcept] = useState('');

  const refresh = useCallback(() => {
    api.get(`/public/caseta/admin/${slug}/bonos?t=${token}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], count: 0, total_balance_eur: 0 }));
  }, [slug, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const create = async () => {
    if (!holderName.trim() || !amount) {
      toast.error('Nombre y importe obligatorios');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/public/caseta/admin/${slug}/bonos?t=${token}`, {
        holder_name: holderName.trim(),
        holder_phone: phone.trim() || null,
        amount_eur: parseFloat(amount),
      });
      toast.success('Bono creado');
      setHolderName('');
      setPhone('');
      setAmount('');
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo crear');
    } finally {
      setSaving(false);
    }
  };

  const consume = async (bonoId: string) => {
    if (!consumeAmt || !consumeConcept) {
      toast.error('Importe y concepto');
      return;
    }
    try {
      await api.post(`/public/caseta/admin/${slug}/bonos/${bonoId}/consume?t=${token}`, {
        amount_eur: parseFloat(consumeAmt),
        concept: consumeConcept,
      });
      toast.success('Consumo registrado');
      setConsumeFor(null);
      setConsumeAmt('');
      setConsumeConcept('');
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo registrar');
    }
  };

  if (!data) return <p className="text-ink-500 text-[13px]">Un momento…</p>;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi label="Bonos activos" value={data.count} accent="brand" />
        <Kpi label="Saldo total" value={`${data.total_balance_eur.toFixed(2)} €`} accent="olive" />
        <Kpi label="Recargado total" value={`${data.total_topped_up_eur.toFixed(2)} €`} accent="terra" />
      </div>

      {/* Crear */}
      <div className="bg-paper border border-line rounded-card p-5">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-3">
          · Nuevo bono
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input
            type="text"
            value={holderName}
            onChange={(e) => setHolderName(e.target.value)}
            placeholder="Nombre del titular"
            className="cc-input md:col-span-2"
          />
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Móvil (para QR por SMS)"
            className="cc-input"
          />
          <input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Recarga inicial €"
            className="cc-input"
          />
        </div>
        <button
          type="button"
          onClick={create}
          disabled={saving}
          className="mt-3 px-4 py-2 rounded-xl bg-brand text-paper font-semibold text-[13px] hover:bg-brand-700 transition disabled:opacity-60"
        >
          {saving ? 'Creando…' : 'Crear bono'}
        </button>
      </div>

      {/* Lista */}
      <div className="bg-paper border border-line rounded-card p-5">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-3">
          · Bonos activos · {data.items.length}
        </div>
        {data.items.length === 0 ? (
          <p className="text-[13px] text-ink-500">Aún no hay bonos.</p>
        ) : (
          <ul className="divide-y divide-line-soft">
            {data.items.map((b: any) => (
              <li key={b.id} className="py-3">
                <div className="flex items-baseline gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-[14.5px] text-ink truncate">
                      {b.holder_name}
                    </div>
                    <div className="text-[12px] text-ink-500 font-mono">
                      {b.qr_token.slice(0, 12)}… · {b.movements_count} movimientos
                    </div>
                  </div>
                  <div className="font-display font-bold text-[17px] text-olive-700 tabular-nums">
                    {b.balance_eur.toFixed(2)} €
                  </div>
                  <button
                    type="button"
                    onClick={() => setConsumeFor(consumeFor === b.id ? null : b.id)}
                    className="text-[12px] text-brand hover:text-brand-700 font-semibold"
                  >
                    Cobrar →
                  </button>
                </div>
                {consumeFor === b.id && (
                  <div className="mt-3 bg-surface rounded-xl p-3 flex flex-col md:flex-row gap-2">
                    <input
                      type="number"
                      step="0.01"
                      value={consumeAmt}
                      onChange={(e) => setConsumeAmt(e.target.value)}
                      placeholder="Importe €"
                      className="cc-input md:max-w-[120px]"
                    />
                    <input
                      type="text"
                      value={consumeConcept}
                      onChange={(e) => setConsumeConcept(e.target.value)}
                      placeholder="Concepto (Tinto verano, etc)"
                      className="cc-input flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => consume(b.id)}
                      className="px-3 py-2 rounded-xl bg-olive-700 text-paper font-semibold text-[12.5px] hover:opacity-90"
                    >
                      Descontar
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <Style />
    </div>
  );
}


function MesasTab({ slug, token }: { slug: string; token: string }) {
  const [items, setItems] = useState<any[] | null>(null);
  const [tableNumber, setTableNumber] = useState('');
  const [date, setDate] = useState('');
  const [hostName, setHostName] = useState('');
  const [party, setParty] = useState('4');
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => {
    api.get(`/public/caseta/admin/${slug}/mesas?t=${token}`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [slug, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const create = async () => {
    if (!tableNumber || !date || !hostName) {
      toast.error('Mesa, fecha y responsable');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/public/caseta/admin/${slug}/mesas?t=${token}`, {
        table_number: tableNumber,
        reserved_for_date: new Date(date).toISOString(),
        host_name: hostName,
        party_size: parseInt(party, 10) || 1,
      });
      toast.success('Mesa reservada');
      setTableNumber('');
      setDate('');
      setHostName('');
      setParty('4');
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo reservar');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id: string, newStatus: string) => {
    try {
      await api.patch(`/public/caseta/admin/${slug}/mesas/${id}?t=${token}`, { status: newStatus });
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo cambiar');
    }
  };

  if (!items) return <p className="text-ink-500 text-[13px]">Un momento…</p>;

  // Agrupa por día
  const byDay: Record<string, any[]> = {};
  items.forEach((m) => {
    const d = new Date(m.reserved_for_date).toLocaleDateString('es-ES');
    if (!byDay[d]) byDay[d] = [];
    byDay[d].push(m);
  });

  return (
    <div className="space-y-6">
      <div className="bg-paper border border-line rounded-card p-5">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-3">
          · Nueva reserva
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input
            type="text"
            value={tableNumber}
            onChange={(e) => setTableNumber(e.target.value)}
            placeholder="Mesa (A3, 12…)"
            className="cc-input"
          />
          <input
            type="datetime-local"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="cc-input"
          />
          <input
            type="text"
            value={hostName}
            onChange={(e) => setHostName(e.target.value)}
            placeholder="Responsable"
            className="cc-input"
          />
          <input
            type="number"
            min="1"
            value={party}
            onChange={(e) => setParty(e.target.value)}
            placeholder="Comensales"
            className="cc-input"
          />
        </div>
        <button
          type="button"
          onClick={create}
          disabled={saving}
          className="mt-3 px-4 py-2 rounded-xl bg-brand text-paper font-semibold text-[13px] hover:bg-brand-700 transition disabled:opacity-60"
        >
          {saving ? 'Guardando…' : 'Reservar mesa'}
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-[13px] text-ink-500">Sin reservas aún.</p>
      ) : (
        Object.entries(byDay).map(([day, mesas]) => (
          <div key={day} className="bg-paper border border-line rounded-card p-5">
            <div className="font-display font-semibold text-[15px] text-ink mb-3 capitalize">
              {day}
            </div>
            <ul className="divide-y divide-line-soft">
              {mesas.map((m: any) => {
                const tag = STATUS_TAG[m.status] || STATUS_TAG.reserved;
                const hour = new Date(m.reserved_for_date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                return (
                  <li key={m.id} className="py-2 flex items-baseline gap-3">
                    <div className="font-mono text-[13px] text-ink-700 w-16 shrink-0">{hour}</div>
                    <div className="font-display font-semibold text-[14px] text-ink w-16 shrink-0">{m.table_number}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13.5px] text-ink truncate">{m.host_name}</div>
                      <div className="text-[11.5px] text-ink-500">
                        {m.party_size} pers. {m.notes && `· ${m.notes}`}
                      </div>
                    </div>
                    <span className="px-2 py-0.5 rounded-pill text-[10.5px] font-bold uppercase" style={{ background: tag.bg, color: tag.text }}>
                      {tag.label}
                    </span>
                    {m.status === 'reserved' && (
                      <>
                        <button type="button" onClick={() => setStatus(m.id, 'seated')} className="text-[11.5px] text-olive-700 hover:underline">
                          sentar
                        </button>
                        <button type="button" onClick={() => setStatus(m.id, 'no_show')} className="text-[11.5px] text-brick hover:underline">
                          no show
                        </button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))
      )}
      <Style />
    </div>
  );
}


const STATUS_TAG: Record<string, { label: string; bg: string; text: string }> = {
  reserved: { label: 'Reservada', bg: '#F7EBD2', text: '#8A5F12' },
  seated: { label: 'Sentada', bg: '#E8EEDC', text: '#46602C' },
  cancelled: { label: 'Cancelada', bg: '#F6E0DC', text: '#8A2C22' },
  no_show: { label: 'No show', bg: '#F0EBE2', text: '#6E6557' },
};


function InvitadosTab({ slug, token }: { slug: string; token: string }) {
  const [data, setData] = useState<any>(null);
  const [name, setName] = useState('');
  const [dni, setDni] = useState('');
  const [invitedBy, setInvitedBy] = useState('');
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending'>('all');

  const refresh = useCallback(() => {
    api.get(`/public/caseta/admin/${slug}/invitados?t=${token}&only_pending=${filter === 'pending'}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], checked_in: 0, pending: 0, count: 0 }));
  }, [slug, token, filter]);

  useEffect(() => { refresh(); }, [refresh]);

  const create = async () => {
    if (!name.trim()) {
      toast.error('Nombre');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/public/caseta/admin/${slug}/invitados?t=${token}`, {
        name: name.trim(),
        dni: dni.trim() || null,
        invited_by: invitedBy.trim() || null,
      });
      toast.success('Invitado añadido');
      setName('');
      setDni('');
      setInvitedBy('');
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo añadir');
    } finally {
      setSaving(false);
    }
  };

  const checkin = async (id: string) => {
    try {
      await api.post(`/public/caseta/admin/${slug}/invitados/${id}/checkin?t=${token}&by_operator=puerta`);
      toast.success('Check-in');
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo hacer check-in');
    }
  };

  const remove = async (id: string) => {
    if (!confirm('¿Quitar invitado?')) return;
    try {
      await api.delete(`/public/caseta/admin/${slug}/invitados/${id}?t=${token}`);
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo borrar');
    }
  };

  if (!data) return <p className="text-ink-500 text-[13px]">Un momento…</p>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Total" value={data.count} accent="brand" />
        <Kpi label="Han entrado" value={data.checked_in} accent="olive" />
        <Kpi label="Falta entrar" value={data.pending} accent="terra" />
      </div>

      <div className="bg-paper border border-line rounded-card p-5">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-3">
          · Añadir invitado
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre" className="cc-input" />
          <input type="text" value={dni} onChange={(e) => setDni(e.target.value)} placeholder="DNI (opcional)" className="cc-input" />
          <input type="text" value={invitedBy} onChange={(e) => setInvitedBy(e.target.value)} placeholder="Invitado por" className="cc-input" />
        </div>
        <button
          type="button"
          onClick={create}
          disabled={saving}
          className="mt-3 px-4 py-2 rounded-xl bg-brand text-paper font-semibold text-[13px] hover:bg-brand-700 transition disabled:opacity-60"
        >
          {saving ? 'Guardando…' : 'Añadir'}
        </button>
      </div>

      <div className="bg-paper border border-line rounded-card p-5">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500">
            · Lista · {data.items.length}
          </div>
          <div className="flex gap-1">
            <button onClick={() => setFilter('all')} className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase ${filter === 'all' ? 'bg-brand text-paper' : 'bg-surface text-ink-700'}`}>todos</button>
            <button onClick={() => setFilter('pending')} className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase ${filter === 'pending' ? 'bg-brand text-paper' : 'bg-surface text-ink-700'}`}>pendientes</button>
          </div>
        </div>
        {data.items.length === 0 ? (
          <p className="text-[13px] text-ink-500">Sin invitados.</p>
        ) : (
          <ul className="divide-y divide-line-soft">
            {data.items.map((inv: any) => (
              <li key={inv.id} className="py-2.5 flex items-baseline gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-[14px] text-ink truncate">{inv.name}</div>
                  <div className="text-[11.5px] text-ink-500 truncate">
                    {inv.dni && `${inv.dni} · `}
                    {inv.invited_by && `por ${inv.invited_by}`}
                    {inv.checked_in_at && ` · entró ${new Date(inv.checked_in_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`}
                  </div>
                </div>
                {inv.checked_in_at ? (
                  <span className="px-2 py-0.5 rounded-pill text-[10.5px] font-bold uppercase bg-olive-50 text-olive-700">dentro</span>
                ) : (
                  <button onClick={() => checkin(inv.id)} className="px-3 py-1 rounded-pill text-[11.5px] font-bold uppercase bg-brand text-paper hover:bg-brand-700">
                    Check-in
                  </button>
                )}
                <button onClick={() => remove(inv.id)} className="text-ink-400 hover:text-brick text-[16px]" aria-label="Borrar">×</button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Style />
    </div>
  );
}


function Kpi({ label, value, accent }: { label: string; value: string | number; accent: 'brand' | 'olive' | 'terra' }) {
  const color = accent === 'olive' ? 'text-olive-700' : accent === 'terra' ? 'text-terra-700' : 'text-brand-700';
  return (
    <div className="bg-paper border border-line rounded-card p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-500 mb-1">· {label}</div>
      <div className={`font-display font-bold text-[20px] tabular-nums tracking-[-0.01em] ${color}`}>{value}</div>
    </div>
  );
}


function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased flex items-center justify-center p-8">
      <div className="text-center">
        <h1 className="font-display font-bold text-[24px] text-ink mb-2">{title}</h1>
        <p className="text-ink-500 text-[14px]">{children}</p>
      </div>
    </div>
  );
}


function Style() {
  return (
    <style>{`
      .cc-input{
        width:100%;
        padding:11px 14px;
        border:1.5px solid #DDD3C4;
        border-radius:11px;
        background:#FFFFFF;
        font-family:inherit;
        font-size:14px;
        color:#26201A;
        outline:none;
        transition:border-color .15s ease;
      }
      .cc-input:focus{ border-color:#1C5D82; }
      .cc-input::placeholder{ color:#A89D8E; }
    `}</style>
  );
}
